import api from '@/api.ts'
import {
  LOCAL_STORAGE_KEY_VERIFY_RECAPTCHA_RESULT,
  RecaptchaActions,
  SESSION_STORAGE_KEY_PV_COUNT,
} from '@/constants'

/** reCAPTCHA Enterprise のグローバル（スクリプト読み込み後に利用） */
declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void
    execute: (siteKey: string, options: { action: string }) => Promise<string>
  }
}

/**
 * Google reCAPTCHA Enterprise のフロント側ラッパー
 * トークン発行とバックエンド検証（{@link api.verifyRecaptcha}）を扱う
 * 
 * MEMO
 * reCAPTCHAを有効にする場合は、GCP側での設定と、head内にライブラリを読み込むスクリプトを仕込む必要がある
 */
export class Recaptcha {  
  /** isReady 判定の再試行間隔（ms） */
  private static readonly RETRY_INTERVAL_MS = 500

  /** isReady 判定の最大リトライ回数 */
  private static readonly MAX_READY_RETRIES = 4

  /** reCAPTCHA 実行に必要なセッション内PV数（全action共通） */
  private static readonly EXECUTE_PAGE_VIEW_COUNT = 30

  /** シミュレーションモード*/
  private isSimulate: boolean = false

  /* reCAPTCHAの結果を保存するストレージのキー   
   * シミュレーションモードの場合は、サフィックスを付与する
   */
  private storage_key = LOCAL_STORAGE_KEY_VERIFY_RECAPTCHA_RESULT

  /** reCAPTCHA のサイトキー（Enterprise の execute に渡す） */
  private sitekey: string

  /** localStorage に保存する reCAPTCHA 評価結果の形式 */
  private static readonly STORED_RESULT_KEYS = {
    RESULT: 'result',
    ACTION: 'action',
  } as const

  /** reCAPTCHAが有効（サイトキーがある、grecaptcha変数がある）な場合 true */
  private _isEnabled = false

  /** grecaptcha.enterprise.ready 完了後に true */
  private _isReady = false  

  /** reCAPTCHAの評価が有効か */
  get isEnabled(): boolean {
    return this._isEnabled
  }

  /** API 利用可否（読み取り専用、内部は `setReady()` で更新） */
  get isReady(): boolean {
    return this._isReady
  }

  /**
   * コンストラクタ
   * サイトキーの初期化、`grecaptcha.enterprise` の有無確認、
   * readyコールバック登録までを行う
   */
  constructor() {
    this.isSimulate = storelocator?.recaptcha_site_key === 'simulate'
    this.storage_key = this.isSimulate ? `${LOCAL_STORAGE_KEY_VERIFY_RECAPTCHA_RESULT}_simulate` : LOCAL_STORAGE_KEY_VERIFY_RECAPTCHA_RESULT
    this.sitekey = storelocator?.recaptcha_site_key || null

    // シミュレーションモードは管理画面からタグ入れなくても利用可
    if (this.isSimulate) {
      this._isEnabled = true
      this.setReady(true)
      return 
    }

    if (!this.hasEnterprise() || !this.sitekey) {
      return
    }

    this._isEnabled = true

    // grecaptchaの準備が完了したら準備完了フラグを更新
    grecaptcha.enterprise.ready(() => {
      this.setReady(true)
    })
  }

  /**
   * `grecaptcha.enterprise` が利用可能か判定する
   * @returns 利用可能な場合は true
   */
  private hasEnterprise(): boolean {
    return typeof grecaptcha !== 'undefined' && !!grecaptcha.enterprise
  }

  /**
   * ライブラリの準備完了状態を更新する（主に ready コールバックから呼ぶ）
   * @param isReady - 実行可能になったら true
   */
  private setReady(isReady: boolean) {
    this._isReady = isReady
  }

  /**
   * reCAPTCHA実行条件を満たしているか判定する
   *
   * 当該のアクションに対して、トークンを発行する前に呼び出す
   * 現状は地図読み込みのみ対応 他のアクションに対応する場合はここに追記する
   * 
   * @param action アクション名
   * @returns 実行条件を満たす場合は true
   */
  isApplyRecaptcha(action: string): boolean {
    // 地図読み込み時のreCAPTCHA実行条件
    // セッション内ページ表示数が規定値以上で、まだreCAPTCHAを実行していない場合のみ発火
    if (action === RecaptchaActions.MAP_SHOW_MAP || action === RecaptchaActions.POINT_SHOW_MAP) {
      const pvCount = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_PV_COUNT) || '0', 10)
      const latestRecaptchaResult = this.getRecaptchaResult()

      // console.debug('有効化判定', {
      //     action: action,
      //     result: latestRecaptchaResult === 'beforeVerify' && pvCount >= Recaptcha.EXECUTE_PAGE_VIEW_COUNT,
      //     latestRecaptchaResult: latestRecaptchaResult,
      //     excute_count:Recaptcha.EXECUTE_PAGE_VIEW_COUNT,
      //     pvCount: pvCount,          
      // })

      return latestRecaptchaResult === 'beforeVerify' && pvCount >= Recaptcha.EXECUTE_PAGE_VIEW_COUNT
    }

    // アクション名不一致
    return false
  }

  /**
   * アクショントークンを取得する
   * bgrecaptchaの準備(isReady)がまだの場合 500ms 間隔で最大4回まで再確認する
   * 
   * @param action アクション名（未指定時は空文字）
   * @returns トークン文字列 | 'cancelled'（ユーザーがキャンセルした場合） | undefined（未準備/未設定の場合）
   */
  async publishToken(action: string): Promise<string | 'cancelled' | undefined> {
    // isReady確認
    for (let retryCount = 0; retryCount < Recaptcha.MAX_READY_RETRIES && !this.isReady; retryCount += 1) {
      await new Promise(resolve => setTimeout(resolve, Recaptcha.RETRY_INTERVAL_MS))
    }

    if (!this.isReady) {
      console.warn('reCAPTCHA is not ready after retries')
      return undefined
    }

    if (!this.sitekey) {
      console.warn('reCAPTCHA sitekey is not set')
      return undefined
    }

    if (!action) {
      console.warn('reCAPTCHA action is not set')
      return undefined
    }

    let token

    if (this.isSimulate) {
      token = 'simulateDummyToken'
    } else {
      token = await grecaptcha.enterprise.execute(this.sitekey, { action: action }).catch(e => {        
        // codeとか返ってこないので文字列比較で判定。ErrorオブジェクトのmessageとString直接の両方を確認する
        const cancelMessage = "Challenge cancelled by user."
        if (e === cancelMessage || e?.message === cancelMessage) {
          return 'cancelled'
        } else {
          console.error('Failed to execute reCAPTCHA', e)
          return undefined
        }
      })
    }

    // トークンが発行された場合はGAイベント送信
    const sendValue = !token ? 'failed'
      : token === 'cancelled' ? 'cancelled'
      : 'published'


    storelocator.analytics.client?.send('RecaptchaPublishToken', {
      RecaptchaPublishAction: action,
      RecaptchaPublishToken: sendValue,
      RecaptchaPublishPage: storelocator.page,
    })

    return token
  }

  /**
   * バックエンドでトークンを検証する
   * バックエンド側で何らかの理由で検証が出来なかった場合は成功扱いで null を返す
   * 検証が完了した場合のみ、評価結果をローカルストレージへ保存する
   * @param token reCAPTCHA トークン
   * @param action アクション名
   * @returns 認証結果のBoolean値 意図せず評価ができなかった場合はnull
   */
  async verify(token: string, action: string = ""): Promise<boolean | null> {
    const response = await api.verifyRecaptcha(token, action)
    const responseValue = response?.data?.success

    this.setRecaptchaResult(action, responseValue)

    // reCAPTCHA結果のGAイベント送信
    storelocator.analytics.client?.send('RecaptchaVerify', {
      RecaptchaVerifyAction: action,
      RecaptchaVerifyResult:
        responseValue === true ? 'true'
        : responseValue === false ? 'false'
        : 'failed',
      RecaptchaVerifyChallenge: response?.data?.challenge ?? null,
      RecaptchaVerifyPage: storelocator.page,
    })
    
    return responseValue
  }

  /**
   * reCAPTCHA の評価結果をローカルストレージに保存する
   * 保存形式: { result: boolean | null, action: string }
   * @param action reCAPTCHA アクション名
   * @param result true: 成功 / false: 失敗 / null: 判定失敗
   */
  setRecaptchaResult(action: string, result: boolean | null): void {
    try {
      localStorage.setItem(
        this.storage_key,
        JSON.stringify({
          [Recaptcha.STORED_RESULT_KEYS.RESULT]: result,
          [Recaptcha.STORED_RESULT_KEYS.ACTION]: action,
        })
      )
    } catch (error) {
      console.error('Failed to save reCAPTCHA result to localStorage', {
        key: this.storage_key,
        action,
        result,
        error,
      })
    }
  }

  /**
   * ローカルストレージから reCAPTCHA の評価結果を取得する
   * - 保存済みの最新評価（result）を返す
   * - アクションは問わず、セッションが切れていたら結果を破棄する
   *
   * @returns 判定前 = 'beforeVerify', 判定後 = boolean, 判定失敗 = null
   */
  getRecaptchaResult(): boolean | 'beforeVerify' | null {
    try {
      // pvCountが規定値以下（セッションが切れた）の場合reCAPTCHA結果を破棄
      const pvCount = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_PV_COUNT) || '0', 10)
      if (pvCount < Recaptcha.EXECUTE_PAGE_VIEW_COUNT) {
        localStorage.removeItem(this.storage_key)
      }

      const storedValue = localStorage.getItem(this.storage_key)
      
      // 値が存在しなかったら評価前とする
      if (!storedValue) {
        return 'beforeVerify'
      }

      // 不正な値が入っていた場合は評価前とする
      const parsedValue = JSON.parse(storedValue) as Record<string, unknown>
      if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
        return 'beforeVerify'
      }

      const storedAction = parsedValue[Recaptcha.STORED_RESULT_KEYS.ACTION]
      const storedResult = parsedValue[Recaptcha.STORED_RESULT_KEYS.RESULT]

      // 新保存形式: { result: boolean | null, action: string }
      if (typeof storedAction !== 'string') {
        return 'beforeVerify'
      }

      if (typeof storedResult === 'boolean' || storedResult === null) {
        return storedResult as boolean | null
      }

      return 'beforeVerify'
      
    } catch (error) {
      // 値取得に失敗した場合はreCAPTCHA評価前として"beforeVerify"を返す
      console.error('Failed to read reCAPTCHA result from localStorage', {
        key: this.storage_key,
        error,
      })
      return 'beforeVerify'
    }
  }

  /**
   * analytics送信用に値を返す
   */
  getAnalyticsContextValues () {
    const recaptchaResult = this.getRecaptchaResult()
    const sendValue = recaptchaResult === true ? 'true'
      : recaptchaResult === false ? 'false'
      : 'beforeVerify'    

    return sendValue
  }
}


export default new Recaptcha()
